import * as React from 'react';
import { useTranslation } from "react-i18next";
import { makeStyles } from '@material-ui/styles';
import { Grid, Icon, Typography } from '@material-ui/core';
import LaptopIcon from '@material-ui/icons/Laptop';
import DesktopWindowsIcon from '@material-ui/icons/DesktopWindows';
import PrintIcon from '@material-ui/icons/Print';
import StorageIcon from '@material-ui/icons/Storage';
import DevicesOtherIcon from '@material-ui/icons/DevicesOther';
import RouterIcon from '@material-ui/icons/Router';
import SdStorageIcon from '@material-ui/icons/SdStorage';
import PersonIcon from '@material-ui/icons/Person';
import DeviceHubIcon from '@material-ui/icons/DeviceHub';
import AlbumIcon from '@material-ui/icons/Album';
import KeyboardIcon from '@material-ui/icons/Keyboard';
import DescriptionIcon from '@material-ui/icons/Description';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import ThumbUpIcon from '@material-ui/icons/ThumbUp';

import clsx from 'clsx';
import { IDeviceListItem, IItSystemListItem, IPersonListItem, IRelatedDeviceListItem, IServiceListItem } from './IModels';
import { transformIcon } from './LogPlusIconWrapper';

const useStyles = makeStyles(() => ({
	icon: {
		color: '#888',
		marginRight: '14px',
	},
	additionalItem: {
		fontStyle: 'italic'
	},
	name: {
		whiteSpace: 'nowrap',
		maxWidth: '400px',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
	propertyValues: {
		display: 'inline-block',
		verticalAlign: 'middle',
		marginRight: '5px',
	},
	propertyValue: {
		maxWidth: '150px',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		display: 'inline-block',
		verticalAlign: 'middle',
		whiteSpace: 'nowrap',	}
}));

interface ILogPlusKindIconProps {
	kind: string,
	type: string,
	className?: string
}

export function LogPlusKindIcon(props: ILogPlusKindIconProps) {
	return (<>
		{props.kind == "Computer" && props.type !== "Server" && <LaptopIcon className={props.className} />}
		{props.kind == "Computer" && props.type === "Server" && <StorageIcon className={props.className} />}

		{props.kind == "Monitor" && <DesktopWindowsIcon className={props.className} />}
		{props.kind == "Printer" && <PrintIcon className={props.className} />}
		{props.kind == "MobileDevice" && <DevicesOtherIcon className={props.className} />}
		{props.kind == "DataCarrier" && <AlbumIcon className={props.className} />}
		{props.kind == "NetworkDevice" && <RouterIcon className={props.className} />}
		{props.kind == "Accessory" && <KeyboardIcon className={props.className} />}
		{props.kind == "PhoneNumber" && <SdStorageIcon className={props.className} />}

		{props.kind == "Person" && <PersonIcon className={props.className} />}
		{props.kind == "ItSystem" && <DeviceHubIcon className={props.className} />}
		{props.kind == "Agreement" && <DescriptionIcon className={props.className} />}
		{props.kind == "License" && <VpnKeyIcon className={props.className} />}
		{props.kind == "Service" && <ThumbUpIcon className={props.className} />}
	</>);
}

interface ILogPlusServiceAutocompleteOptionProps {
	name: string,
	option: IServiceListItem,
	searchIn?: (keyof IServiceListItem)[],
}

export function LogPlusServiceAutocompleteOption(props: ILogPlusServiceAutocompleteOptionProps) {
	const classes = useStyles();
	const { t } = useTranslation();

	let additionalElements = null;

	if (props.option) {
		var properties = props.searchIn?.filter(prop => !!props.option[prop]) ?? [];

		var propertiesNodes = properties.map((prop, i) => <span key={prop} data-property={prop}>
			{props.option[prop]}{i != properties.length - 1 && ', '}
		</span>);

		if (propertiesNodes && propertiesNodes.length > 0) {
			additionalElements = <> ({propertiesNodes}) </>
		}
	}

	return (<Grid container alignItems="center">
		<Grid item>
			{props.option && <Icon className={classes.icon} >{transformIcon(props.option?.Icon)}</Icon>}
		</Grid>
		<Grid item xs className={clsx(props.name[0] === '<' && classes.additionalItem)}>
			<div title={props.name} className={classes.name}>{props.name}</div>
			{props.option && !additionalElements && <Typography variant="body2" color="textSecondary">
				{props.option.ServiceProfileGroupName}
				{' / '}
				{props.option.ServiceProfileName}
			</Typography>}
			{additionalElements && <Typography variant="body2" color="textSecondary">
				{additionalElements}
			</Typography>}
		</Grid>
	</Grid>);
}

interface IProps {
	kind: string,
	type: string,
	name: string,

	searchIn?: string[],
	item?: IDeviceListItem | IPersonListItem | IItSystemListItem | IRelatedDeviceListItem,
}

export default function LogPlusAutocompleteOption(props: IProps) {
	const classes = useStyles();
	const { t } = useTranslation();

	let additionalElements = null;

	if (props.item && props.searchIn && props.searchIn.length > 0) {
		const properties = props.searchIn?.filter(prop => !!props.item[prop]) ?? [];
		const propertiesNodes = properties.map((prop, i) => (
			<span key={prop} data-property={prop} title={props.item[prop]} className={classes.propertyValues} >
				<span className={classes.propertyValue}>{props.item[prop]}</span>
				{i != properties.length - 1 && ', '}
			</span>
		));

		if (propertiesNodes && propertiesNodes.length > 0) {
			additionalElements = <> ({propertiesNodes}) </>
		}
	}

	let typeLabel = translateType(t, props.kind, props.type);
	
	return (<Grid container alignItems="center">
		<Grid item>
			{props.kind == "Computer" && props.type !== "Server" && <LaptopIcon className={classes.icon} />}
			{props.kind == "Computer" && props.type === "Server" && <StorageIcon className={classes.icon} />}

			{props.kind == "Monitor" && <DesktopWindowsIcon className={classes.icon} />}
			{props.kind == "Printer" && <PrintIcon className={classes.icon} />}
			{props.kind == "MobileDevice" && <DevicesOtherIcon className={classes.icon} />}

			{props.kind == "DataCarrier" && <AlbumIcon className={classes.icon} />}
			{props.kind == "NetworkDevice" && <RouterIcon className={classes.icon} />}

			{props.kind == "Accessory" && <KeyboardIcon className={classes.icon} />}
			{props.kind == "PhoneNumber" && <SdStorageIcon className={classes.icon} />}
		</Grid>
		<Grid item xs className={clsx(props.name[0] === '<' && classes.additionalItem)}>
			<div title={props.name} className={classes.name}>{props.name}</div>
			<Typography variant="body2" color="textSecondary">
				{typeLabel}
				{additionalElements}
			</Typography>
		</Grid>
	</Grid>);
}

export function translateType(t: (s: string) => string, hdKind: string, type: string): string {
	
	if (!type) {
		return "";
	}

	if (hdKind == "License") {
		return "";
	}
	let key = "";
	if (hdKind) {
		key = "logPlusResources.type." + hdKind + "." + type;
		let trans = t(key);
		if (key != trans)
			return trans;
	}

	let keyDefault = "logPlusResources.type.default." + type;
	let trans = t(keyDefault);
	if (keyDefault != trans)
		return trans;
	
	console.warn("No translations found for '" + key + "'.");
	return "";
}